<template>
  <family-page-wrapper class="family-reorder-delivery">
    <store-checkout
      :store-group="selectedStoreGroup"
      :delivery-address="deliveryAddress"
      :all-items-from-the-same-menu="getMenuIds(estimate).length === 1"
      :cart="cart"
      :copy-order="copyOrder"
      :additional-charges="additionalCharges"
      :contact-info="contactInfo"
      :estimate="estimate"
      :order-options="orderOptions"
      :is-estimate-loading="status.isEstimateLoading"
      :estimate-failed="status.estimateFailed"
      :charge-options="chargeOptions"
      @clear-checkout="cancelReorder"
      @refresh-estimate="refreshEstimate"
      @change-charge-options="onChangeChargeOptions"
    />
  </family-page-wrapper>
</template>

<script lang="ts">
import { ref, Ref, reactive, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import delay from 'yoctodelay';
import { ElLoading, ElMessageBox } from 'element-plus';
import { map, uniq } from 'lodash';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';

import StoreCheckout from '@/components/Main/Operator/Delivery/StoreCheckout.vue';
import { gourmetGetEstimate, GourmetGetEstimateOutput } from '@/services/gourmet/gourmetGetEstimate';
import { gourmetService } from '@/services/gourmet';
import type { DeliveryEstimateType } from '@/models/gourmet';

const defaultChargeOptions = {
  applyDiscountedFee: true,
  noServiceFee: false,
  doNotCharge: false,
  chargeNow: false,
  reason: `Family dashboard order`,
};

export default {
  name: 'ReorderDelivery',

  components: {
    StoreCheckout,
  },

  setup: () => {
    const route = useRoute();
    const router = useRouter();

    const { customerInfo } = useCustomer();
    const { showErrorPopupDialog } = useSwalDialog();

    const selectedStoreGroup: Ref<any> = ref(null);
    const deliveryAddress: Ref<any> = ref(null);
    const estimate: Ref<GourmetGetEstimateOutput | { [k in string]: undefined }> = ref({});
    const copyOrder: Ref<any> = ref(null);
    const cart: Ref<any> = ref([]);
    const additionalCharges: Ref<{ tip: number }> = ref({
      tip: 0,
    });
    const contactInfo: Ref<{
      preferredPhoneNumber: string;
      clientName: string;
    }> = ref({
      preferredPhoneNumber: '',
      clientName: '',
    });
    const chargeOptions: Ref<typeof defaultChargeOptions> = ref(defaultChargeOptions);
    const orderOptions: Ref<any> = ref({});

    const status = reactive({
      isEstimateLoading: false,
      estimateFailed: false,
    });

    const paramCopyId = computed(() => route.params.copyId);

    const loadOrderEstimate = async (orderId: any) => {
      status.isEstimateLoading = true;

      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Order Items...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      const response = await gourmetService.getOrdersWithEstimation({
        where: {
          id: orderId,
        },
        pagination: {
          limit: 1,
          offset: 0,
        },
      });

      const order = response.rows[0];
      copyOrder.value = order;

      const orderEstimate = order.storeOrder.estimate as DeliveryEstimateType;
      deliveryAddress.value = orderEstimate.address;
      additionalCharges.value = {
        tip: (orderEstimate.additionalCharges.tip ?? 0) / 100,
      };
      contactInfo.value = {
        preferredPhoneNumber: order.preferredPhoneNumber ?? '',
        clientName: order.clientName ?? '',
      };
      chargeOptions.value = defaultChargeOptions;
      cart.value = orderEstimate.items;

      delete orderEstimate.callSid;

      if (order.storeOrder.storeGroup) {
        selectedStoreGroup.value = order.storeOrder.storeGroup;
      } else {
        selectedStoreGroup.value = {
          title: order.storeOrder.title,
        };
      }

      orderOptions.value = orderEstimate.orderOptions;
      await refreshEstimate();

      status.isEstimateLoading = false;
      loading.close();
    };

    const refreshEstimate = async () => {
      estimate.value = await getEstimate(cart.value, additionalCharges.value, null, chargeOptions.value);
    };

    const getEstimate = async (cartItems: any, additionalChargeOptions: any, promotion: any = null, chargeOptionsValue: any = {}) => {
      status.isEstimateLoading = true;
      status.estimateFailed = false;

      await delay(4000);

      if (!promotion && estimate.value && estimate.value.promotion) {
        promotion = estimate.value.promotion;
      }

      const address = { ...deliveryAddress.value };

      if (!deliveryAddress.value.postalCode || deliveryAddress.value.zip) {
        address.postalCode = deliveryAddress.value.zip;
      }

      if (address.zip !== undefined) {
        delete address.zip;
      }

      try {
        const response = await gourmetGetEstimate({
          callerId: customerInfo.value.id,
          items: cartItems,
          address,
          additionalCharges: {
            tip: additionalChargeOptions.tip * 100,
            shouldTipBeSubsidized: additionalChargeOptions.shouldTipBeSubsidized
          },
          chargeOptions: chargeOptionsValue,
          promotion,
        });

        if (response.errors) {
          const errorText = util.getGraphqlError(
            {
              graphQLErrors: response.errors,
            },
            false,
          );

          ElMessageBox.alert(errorText, '', {
            confirmButtonText: 'Close',
            type: 'error',
            showClose: false,
          });

          status.estimateFailed = true;
          status.isEstimateLoading = false;

          return {};
        }

        const estimateValue = response.data.getEstimate;
        if (getMenuIds(estimateValue).length > 1) {
          showErrorPopupDialog({
            text: 'This order contains items from different menus. Please clear the cart and pick items again.',
          });
        }

        status.isEstimateLoading = false;
        return estimateValue;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        ElMessageBox.alert(errorText, '', {
          confirmButtonText: 'Close',
          type: 'error',
          showClose: false,
        });

        status.estimateFailed = true;
        status.isEstimateLoading = false;
        return {};
      }
    };

    const getMenuIds = (estimateValue: any) => {
      return uniq(map(estimateValue?.items || [], 'item.serviceItemReference.menuId'));
    };

    const onChangeChargeOptions = (data: any) => {
      if (
        chargeOptions.value.doNotCharge !== data.doNotCharge ||
        chargeOptions.value.chargeNow !== data.chargeNow ||
        chargeOptions.value.noServiceFee !== data.noServiceFee ||
        chargeOptions.value.applyDiscountedFee !== data.applyDiscountedFee
      ) {
        chargeOptions.value = data;
        if (cart.value.length) {
          refreshEstimate();
        }
      } else {
        chargeOptions.value = data;
        estimate.value = {
          ...(estimate.value as GourmetGetEstimateOutput),
          chargeOptions: {
            ...(estimate.value?.chargeOptions ?? {}),
            ...data,
          },
        };
      }
    };

    const cancelReorder = () => {
      router.push('/family/gourmet-history');
    };

    watch(
      additionalCharges,
      () => {
        if (cart.value.length) {
          refreshEstimate();
        }
      },
      {
        deep: true,
      },
    );

    watch(
      paramCopyId,
      (value: any) => {
        if (!value) {
          return;
        }

        loadOrderEstimate(value);
      },
      { immediate: true },
    );

    return {
      selectedStoreGroup,
      deliveryAddress,
      cart,
      copyOrder,
      additionalCharges,
      contactInfo,
      orderOptions,
      chargeOptions,
      estimate,
      status,

      refreshEstimate,
      onChangeChargeOptions,
      getMenuIds,
      cancelReorder,
    };
  },
};
</script>
